import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Phone } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { getCalApi } from "@calcom/embed-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    feedbackType: "",
    message: "",
  });

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "default" });
      cal("ui", {
        cssVarsPerTheme: {
          light: { "cal-brand": "#007ee5" },
          dark: { "cal-brand": "#fafafa" }
        },
        hideEventTypeDetails: false,
        layout: "month_view"
      });
    })();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    toast.success("Message sent successfully!");
    console.log(formData);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="container mx-auto px-4 py-12 sm:py-20">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Get in Touch
            </h1>
            <p className="text-muted-foreground text-lg">
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-medium">
                  Name
                </Label>
                <Input
                  id="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 border-2 rounded-xl"
                  required
                />
                <p className="text-muted-foreground text-sm">
                  Enter your full name
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="example@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12 border-2 rounded-xl"
                  required
                />
                <p className="text-muted-foreground text-sm">
                  Enter your email address
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedbackType" className="font-medium">
                Feedback Type
              </Label>
              <Select
                value={formData.feedbackType}
                onValueChange={(value) => setFormData({ ...formData, feedbackType: value })}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl">
                  <SelectValue placeholder="Select feedback type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="struggling">😫 I'm struggling with this in Shattara AI...</SelectItem>
                  <SelectItem value="love">😍 I love this about Shattara AI...</SelectItem>
                  <SelectItem value="feature">😇 I wish Shattara AI had this feature...</SelectItem>
                  <SelectItem value="bug">😵‍💫 I found a bug in Shattara AI...</SelectItem>
                  <SelectItem value="thoughts">🤔 My overall thoughts on Shattara AI...</SelectItem>
                  <SelectItem value="other">💬 Something else...</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-sm">
                Choose the type of feedback
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="font-medium">
                Message
              </Label>
              <Textarea
                id="message"
                placeholder="Describe your message in detail..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="min-h-64 border-2 rounded-xl resize-y"
                required
              />
              <p className="text-muted-foreground text-sm">
                Describe your message in detail
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
              >
                Submit
                <Send className="w-4 h-4 ml-2" />
              </Button>
              
              <Button
                type="button"
                variant="outline"
                data-cal-namespace="default"
                data-cal-link="shattaraai/product-walkthrough"
                data-cal-origin="https://cal.id"
                data-cal-config='{"layout":"month_view"}'
                className="w-full h-12 rounded-xl border-2 font-medium hover:bg-accent"
              >
                Schedule
                <Phone className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
